import Image from '../Image';
import { News } from '../News/types';
import SwiperOverflowHidden from '../Swiper/SwiperOverflowHidden';
import { BoardCommonControllerService } from '@/__generated__/CommonApi';
import {
  BOARD_KIND_NOTICE,
  BOARD_KIND_PRESS,
  BOARD_KIND_FIELDTRIP,
  BOARD_KIND_NEWS,
  BOARD_KIND_PUBLIC,
  BOARD_KIND_STORY,
} from '@/helpers/BoardHelper';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { Link } from 'gatsby';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import SwiperCore, { A11y, Navigation, Scrollbar } from 'swiper';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/scrollbar/scrollbar.min.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';

SwiperCore.use([Navigation, Scrollbar, A11y]);

interface BoardProps {
  boards: string[];
  baseUri: string;
  queryString: string;
}

export interface BoardData {
  id: string | number;
  subject: string | undefined;
  contents?: string | undefined;
  link?: string;
}

export const CardLayout = styled.div`
  width: 100%;
`;

export const CardImage = styled.div`
  width: 100%;
  overflow: hidden;
  border: solid 1px #e5e6e8;

  .img-box {
    padding-top: 100%;
  }
`;

export const CardSource = styled(Image)`
  max-width: 100%;
`;
export const CardTextArea = styled.div`
  padding: 24px 24px 0 0;

  h4 {
    font-size: 20px;
    font-weight: bold;
    line-height: 1.6;
    letter-spacing: -1.2px;
    color: #2d2926;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
  }

  h5 {
    margin-bottom: 24px;
    font-size: 16px;
    font-weight: 300;
    line-height: 2;
    letter-spacing: -0.8px;
    color: #2d2926;
  }

  p {
    margin-bottom: 0;
    font-size: 20px;
    font-weight: bold;
    line-height: 1.6;
    letter-spacing: -1.2px;
    color: #2d2926;
  }

  ${breakpoint(`mobile`)} {
    padding: 16px 16px 0 0;

    h4 {
      font-size: 16px;
      line-height: 1.63;
      letter-spacing: -0.8px;
    }

    h5 {
      margin-bottom: 16px;
      font-size: 14px;
    }

    p {
      font-size: 16px;
      line-height: 1.63;
      letter-spacing: -0.8px;
    }
  }
`;

const SwiperCustom = styled(SwiperOverflowHidden)`
  padding: 0;

  .swiper-slide {
    overflow: hidden;
    width: 25%;
    padding: 0 16px;

    ${breakpoint(`tablet`)} {
      width: 33.33%;
      padding: 0 13px;
    }

    ${breakpoint(`mobile`)} {
      width: 50%;
    }
  }
`;

const LinkItem = styled(Link)`
  display: block;
`;

const NewsThumbCard: FC<BoardProps> = ({ boards, baseUri, queryString }) => {
  const [sortedBoards, setSortedBoards] = useState<News[]>([]);

  const loadRelData = useCallback(async () => {
    try {
      const promises = [];
      for (const rel of boards) {
        const res = BoardCommonControllerService.foListAllUsingGet({
          boardCategoryCode: [
            BOARD_KIND_NOTICE,
            BOARD_KIND_PRESS,
            BOARD_KIND_NEWS,
            BOARD_KIND_PUBLIC,
            BOARD_KIND_STORY,
            BOARD_KIND_FIELDTRIP,
          ],
          boardIndexNumber: rel,
        });
        promises.push(res);
      }
      setSortedBoards(
        (await Promise.all(promises))
          .map((dt) => dt.data[0])
          .sort(
            (a: News, b: News) =>
              Number(b.boardIndexNumber) - Number(a.boardIndexNumber),
          ),
      );
    } catch (e) {
      console.error(e);
    }
  }, [boards]);

  useEffect(() => {
    loadRelData();
  }, [loadRelData]);

  return (
    <SwiperCustom>
      <Swiper
        slidesPerView={4}
        navigation
        scrollbar={{ draggable: true }}
        breakpoints={{
          1024: { slidesPerView: 4 },
          320: { slidesPerView: 2 },
        }}
      >
        {sortedBoards.map((board) => {
          if (!board) {
            return null;
          }
          return (
            <SwiperSlide key={board.boardIndexNumber}>
              <LinkItem
                to={`${baseUri}/${board.boardIndexNumber}${queryString}`}
              >
                <CardLayout>
                  <CardImage>
                    <div
                      className="img-box"
                      css={`
                        background-image: url(${board.imgAttGrpNoTn});
                      `}
                    />
                  </CardImage>
                  <CardTextArea>
                    <h4>{board.subject}</h4>
                  </CardTextArea>
                </CardLayout>
              </LinkItem>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </SwiperCustom>
  );
};

export default NewsThumbCard;
